// src/components/admin/MatchListLayer.jsx
import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchInteractions,
  fetchInteractionById,
  deleteInteraction,
  deleteInteractionBulk,
  postMatchOverride,
} from "../services/matchService";
import {
  showConfirmationDialog,
  handleUnauthorized,
} from "../../utils/apiHelper";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { getModulePermissions } from "../../utils/permissionUtils";
import { useDebounce } from "../../hook/useDebounce";

const MatchListLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();


  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500); // 500ms delay

  const [actionFilter, setActionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const headerCheckboxRef = useRef(null);
  const [selectAllGlobal, setSelectAllGlobal] = useState(false);
  const [selectExcluded, setSelectExcluded] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [sortBy, setSortBy] = useState("createdAt"); // default sort
  const [sortOrder, setSortOrder] = useState("desc"); // default order

  const { shouldCheckPermissions, canEdit, canAdd, canView, canDelete } =
    getModulePermissions("Match Control");

  // Fetch list
  const loadData = async (p = page) => {
    setLoading(true);
    try {
      const params = {
        page: p,
        limit,
        search: debouncedSearch || undefined,
        action: actionFilter || undefined,
        status: statusFilter || undefined,
        startDate: startDate
          ? moment(startDate).format("YYYY-MM-DD")
          : undefined,
        endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
        sortBy, // <-- add this
        sortOrder,
      };
      const res = await fetchInteractions(params);
      setItems(res?.data?.data || res.data);
      const pagination = res?.data.pagination || {};
      setTotal(pagination?.total || 0);
      setPage(pagination?.page || p);
      setLimit(pagination?.limit || limit);
    } catch (err) {
      if (err?.response?.status === 401) handleUnauthorized(navigate);
      setErrorMessage(err.message || "Failed to fetch interactions");
    } finally {
      setLoading(false);
    }
  };


  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // 1️⃣ Initialize statusFilter from URL params
    if (!initialized) {
      const params = new URLSearchParams(location.search);
      const urlStatus = params.get("status") || "";
      setStatusFilter(urlStatus);
      setPage(1);
      setInitialized(true);
      return; // stop here, data will load in next effect
    }

    // 2️⃣ Load data whenever filters, search, pagination, or sort change
    loadData(page);
  }, [
    page,
    debouncedSearch,
    actionFilter,
    statusFilter,
    startDate,
    endDate,
    limit,
    sortBy,
    sortOrder,
    location.search,
  ]);


  // Selection helpers
  const isSelected = (id) =>
    selectAllGlobal ? !selectExcluded.has(id) : selectedItems.has(id);

  const handleSelectItem = (id) => {
    if (selectAllGlobal) {
      setSelectExcluded((prev) => {
        const updated = new Set(prev);
        if (updated.has(id)) updated.delete(id);
        else updated.add(id);
        return updated;
      });
      return;
    }
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) updated.delete(id);
      else updated.add(id);
      return updated;
    });
  };

  const currentPageIds = items.map((i) => i._id);
  const isHeaderChecked = () =>
    selectAllGlobal
      ? true
      : currentPageIds.every((id) => selectedItems.has(id));
  const isHeaderIndeterminate = () =>
    !selectAllGlobal &&
    currentPageIds.some((id) => selectedItems.has(id)) &&
    !isHeaderChecked();

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isHeaderIndeterminate();
    }
  }, [items, selectedItems, selectAllGlobal]);

  const handleSelectAllPage = (checked) => {
    setSelectAllGlobal(false);
    setSelectExcluded(new Set());
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      items.forEach((u) => {
        if (checked) updated.add(u._id);
        else updated.delete(u._id);
      });
      return updated;
    });
  };

  const handleSelectAllGlobal = async () => {
    const result = await showConfirmationDialog(
      `You are about to select ALL ${total} interactions. Continue?`,
      "Select All",
      "Cancel"
    );
    if (!result.isConfirmed) return;
    setSelectAllGlobal(true);
    setSelectedItems(new Set());
    setSelectExcluded(new Set());
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirmationDialog(
      "This will delete the interaction",
      "Delete",
      "Cancel"
    );
    if (!confirmed.isConfirmed) return;
    try {
      await deleteInteraction(id);
      setSuccessMessage("Interaction deleted");
      setTimeout(() => setSuccessMessage(""), 3000);
      loadData(page);
    } catch (err) {
      setErrorMessage(err.message || "Failed to delete");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleDeleteAll = async () => {
    let payload;
    if (selectAllGlobal)
      payload = { selectAll: true, exclude: Array.from(selectExcluded) };
    else {
      payload = { ids: Array.from(selectedItems) };
      if (!payload.ids.length) {
        alert("No items selected");
        return;
      }
    }

    const confirmed = await showConfirmationDialog(
      "This will delete selected interactions",
      "Delete",
      "Cancel"
    );
    if (!confirmed.isConfirmed) return;

    try {
      await deleteInteractionBulk(payload);
      setSuccessMessage("Deleted successfully");
      setSelectedItems(new Set());
      setSelectAllGlobal(false);
      setSelectExcluded(new Set());
      setTimeout(() => setSuccessMessage(""), 3000);
      loadData(1);
    } catch (err) {
      setErrorMessage(err.message || "Delete failed");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const handlePageChange = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const start = Math.max(1, page - delta);
    const end = Math.min(totalPages, page + delta);
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const handleSort = (column) => {
    setSortBy(column);
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const sortedItems = [...items].sort((a, b) => {
    const dateA = a.repliedAt || a.createdAt;
    const dateB = b.repliedAt || b.createdAt;
    return sortOrder === "asc"
      ? new Date(dateA) - new Date(dateB)
      : new Date(dateB) - new Date(dateA);
  });

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && <SuccessAlert message={successMessage} />}
      {errorMessage && <DengerAlert message={errorMessage} />}

      {/* Header Filters & Selection */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          {/* Show limit selector */}
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10 items per page</option>
            <option value={20}>20 items per page</option>
            <option value={50}>50 items per page</option>
            <option value={100}>100 items per page</option>
          </select>

          {/* Search */}
          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{
                paddingRight: search ? "5rem" : "2.5rem",
                borderRadius: "7px",
              }}
            />

            {/* Search icon */}
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: search ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />

            {/* Clear button */}
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          {/* Action Filter */}
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Actions</option>
            <option value="curiouzz">Curiouzz</option>
            <option value="super_curiouzz">Super Curiouzz</option>
            <option value="not_curiouzz">Not Curiouzz</option>
          </select>

          {/* Status Filter */}
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="matched">Matched</option>
            <option value="rejected">Rejected</option>
            <option value="blocked">Blocked</option>
            <option value="unmatched">Un Matched</option>
          </select>

          {/* Delete Button */}
          {(!shouldCheckPermissions || canDelete) && (
            <button
              className="btn btn-danger"
              disabled={!selectAllGlobal && selectedItems.size === 0}
              onClick={handleDeleteAll}
            >
              Delete All
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>
                  <div className="form-check style-check d-flex align-items-center">
                    <input
                      className="form-check-input radius-4 border input-form-dark"
                      type="checkbox"
                      ref={headerCheckboxRef}
                      checked={isHeaderChecked()}
                      onChange={(e) => handleSelectAllPage(e.target.checked)}
                      style={{
                        backgroundColor: isHeaderIndeterminate()
                          ? "transparent"
                          : undefined,
                        borderColor: isHeaderIndeterminate()
                          ? "#d1d5db80"
                          : undefined,
                      }}
                    />
                  </div>
                </th>
                <th
                  onClick={() => handleSort("fromUser.email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    From User
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortBy === "fromUser.email" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortBy === "fromUser.email" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("toUser.email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    To User
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortBy === "toUser.email" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortBy === "toUser.email" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Action Status</th>
                <th>Status</th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortBy === "createdAt" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortBy === "createdAt" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : sortedItems.length > 0 ? (
                sortedItems.map((i) => (
                  <tr key={i._id}>
                    <td>
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border border-neutral-400"
                          type="checkbox"
                          value={i._id}
                          checked={isSelected(i._id) || selectAllGlobal}
                          onChange={() => handleSelectItem(i._id)}
                        />
                      </div>
                    </td>

                    <td>{i.fromUser?.email || i.fromUser?._id || "N/A"}</td>
                    <td>{i.toUser?.email || i.toUser?._id || "N/A"}</td>
                    {/* <td>{i.action || "N/A"}</td> */}
                    <td>
                      {i.action
                        ? i.action
                          .replace(/_/g, " ") // replace underscores with space
                          .replace(/\b\w/g, (l) => l.toUpperCase()) // capitalize first letters
                        : "N/A"}
                    </td>
                    <td>
                      <span>
                        {i.status?.charAt(0).toUpperCase() + i.status?.slice(1)}
                      </span>
                    </td>
                    {/* <td>{moment(i.createdAt).format("DD-MM-YYYY")}</td> */}
                    <td>
                      {i.repliedAt
                        ? moment(i.repliedAt).format("DD-MM-YYYY")
                        : moment(i.createdAt).format("DD-MM-YYYY")}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {(!shouldCheckPermissions || canView) && (
                          <button
                            title="View"
                            onClick={() =>
                              navigate(`/match-control/view/${i._id}`)
                            }
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="majesticons:eye-line" className="icon text-xl"
                            />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canEdit) && (
                          <button
                            title="Edit"
                            onClick={() =>
                              navigate(`/match-control/edit/${i._id}`)
                            }
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canDelete) && (
                          <button
                            title="Delete"
                            onClick={() => handleDelete(i._id)}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No interactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {(page - 1) * limit + 1} to{" "}
              {Math.min(page * limit, total)} of {total} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(page - 1)}
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>
              {getPaginationRange().map((p) => (
                <li key={p} className="page-item">
                  <button
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${page === p
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                      }`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${page === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(page + 1)}
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchListLayer;
